import React from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * QuickActionCard component for displaying quick action shortcuts.
 *
 * Features:
 * - Displays an icon, title, description, and action button
 * - Clickable/navigable for common tasks (add domain, create database, etc.)
 * - Supports loading state with skeleton UI
 * - Fully responsive with dark mode support
 */

interface QuickActionCardProps {
  /** The title of the action (e.g., "Add Domain") */
  title: string
  /** Brief description of what the action does */
  description?: string
  /** Icon or emoji to display */
  icon?: string
  /** Label for the action button */
  actionLabel?: string
  /** Click handler for the card/button */
  onClick?: () => void
  /** Optional href for navigation */
  href?: string
  /** Loading state */
  isLoading?: boolean
}

export const QuickActionCard: React.FC<QuickActionCardProps> = ({
  title,
  description,
  icon,
  actionLabel = 'Go',
  onClick,
  href,
  isLoading = false,
}) => {
  const navigate = useNavigate()
  // F08-010 — refuse non-relative hrefs. The prop today is always
  // a hard-coded string from ClientDashboard, but if the source ever
  // becomes URL-derived (a setting, a query param, an API field) the
  // string would land in `window.location.href` verbatim — perfect
  // open-redirect / `javascript:` shape. Restrict to same-origin
  // relative paths beginning with "/" so a hostile prop can't redirect
  // off-site.
  const isSafeHref = (h: string): boolean => {
    if (!h) return false
    if (h.startsWith('//')) return false  // protocol-relative
    if (h.startsWith('/')) return true     // same-origin path
    return false                            // refuse anything else
  }

  const handleClick = () => {
    if (href && isSafeHref(href)) {
      // Client-side navigation: a full reload re-boots the SPA for nothing.
      navigate(href)
    } else if (onClick) {
      onClick()
    } else if (href) {
      // Surface the refusal so the source of an unsafe href is
      // diagnosable in dev tools; production silently no-ops.
      // eslint-disable-next-line no-console
      console.error('QuickActionCard refused unsafe href:', href)
    }
  }

  return (
    <div
      data-testid={`quick-action-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className="card cursor-pointer transition-shadow hover:shadow-md group"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      {isLoading ? (
        <div className="space-y-3">
          <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32"></div>
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {icon && (
            <div className="text-3xl mb-3">
              {icon}
            </div>
          )}

          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>

          {description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex-1">
              {description}
            </p>
          )}

          <div className="mt-3">
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 group-hover:underline">
              {actionLabel} →
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
